import {
  fallbackEmailShell,
  PVR_PRICE,
  tvPackageKeyToManualId,
  tvPackages,
} from './catalogDefaults';
import { formatMoney } from './formatters';
import { PAB_SURCHARGE } from './internetPricing';
import {
  formatMobilityLinePricingNote,
  normalizeMobilityLinePricing,
} from './mobilityPricing';
import { nullableNumberOrFallback, numberOrFallback } from './pricingNumbers';
import { normalizeTvPriceAdjustmentRule } from './tvPricing';

export const normalizeDisplayChipEntry = (chip = {}, index = 0) => ({
    text: String(chip.text || '').trim(),
    type: chip.type || chip.color || 'default',
    visible: chip.visible !== false,
    order: Number.isFinite(Number(chip.order)) ? Number(chip.order) : index + 1
});

export const configuredDisplayChipEntries = (source = {}, fieldNames = []) => {
    for (const fieldName of fieldNames) {
        if (Array.isArray(source[fieldName])) {
            return source[fieldName].map(normalizeDisplayChipEntry);
        }
    }

    return null;
};

export const hasConfiguredDisplayChipEntries = (source = {}, fieldNames = []) => fieldNames.some((fieldName) => Array.isArray(source[fieldName]));

export const visibleChipEntries = (entries = []) => [...entries]
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    .filter((chip) => chip.visible !== false && chip.text);

export const fallbackTopBadgeEntries = (product = {}) => (Array.isArray(product.badges) ? product.badges : [])
    .map((badge, index) => normalizeDisplayChipEntry({
        text: badge,
        type: product.badgeColor || 'primary',
        visible: true,
        order: index + 1
    }, index));

export const visibleDisplayTopBadges = (product = {}) => visibleChipEntries(
    configuredDisplayChipEntries(product.display || {}, ['topBadges', 'cardBadges'])
    ?? fallbackTopBadgeEntries(product)
);

export const visibleDisplayChips = (product = {}, fallbackChips = []) => {
    const configured = configuredDisplayChipEntries(product.display || {}, ['detailChips', 'featureChips', 'chips']);
    const fallback = (fallbackChips || []).map((chip, index) => (
        typeof chip === 'string'
            ? normalizeDisplayChipEntry({ text: chip, visible: true, order: index + 1 }, index)
            : normalizeDisplayChipEntry(chip, index)
    ));

    return visibleChipEntries(configured ?? fallback).map((chip) => chip.text);
};

export const productLifecycleStatus = (product = {}) => {
    if (product.status === 'archived' || product.archivedAt) {
        return 'archived';
    }

    const configuredStatus = product.status || 'active';
    const now = Date.now();
    const startsAt = product.startsAt ? new Date(product.startsAt).getTime() : null;
    const expiresAt = product.expiresAt ? new Date(product.expiresAt).getTime() : null;

    if (Number.isFinite(expiresAt) && expiresAt < now) {
        return 'expired';
    }

    if (Number.isFinite(startsAt) && startsAt > now) {
        return 'scheduled';
    }

    return configuredStatus === 'scheduled' ? 'active' : configuredStatus;
};

export const productIsAvailableForBuilder = (product = {}, options = {}) => {
    const status = productLifecycleStatus(product);

    if (product.visible === false || status === 'archived' || status === 'expired' || status === 'scheduled') {
        return false;
    }

    if (options.isAgentBeta && product.visibleInAgentBuilder === false) {
        return false;
    }

    if (!options.isAgentBeta && product.visibleInAdminBuilder === false) {
        return false;
    }

    return true;
};

export const normalizeConfigHomePhoneProduct = (product, fallbackProduct = {}) => {
    const detailChips = visibleDisplayChips(product, fallbackProduct.chips);
    const topBadges = visibleDisplayTopBadges(product);
    const primaryTopBadge = topBadges[0];
    const internalNotes = Array.isArray(product.internalNotes) ? product.internalNotes : [];
    const fallbackFeatures = internalNotes.join('; ') || fallbackProduct.features || '';
    const cardDescription = product.display?.cardDescription || product.cardDescription || fallbackProduct.cardDescription;

    return {
        ...fallbackProduct,
        id: product.id,
        name: product.displayName || fallbackProduct.name || product.id,
        sortOrder: product.sortOrder ?? fallbackProduct.sortOrder ?? 999,
        price: Number(product.price ?? fallbackProduct.price ?? 0),
        originalPrice: Number(product.regularPrice ?? fallbackProduct.originalPrice ?? product.price ?? 0),
        requiredFees: Number(product.requiredFees ?? fallbackProduct.requiredFees ?? 0),
        features: fallbackFeatures,
        feeNote: product.requiredFees > 0 ? 'Includes required 911 fees' : fallbackProduct.feeNote || '',
        cardDescription: cardDescription || [
            fallbackFeatures,
            product.requiredFees > 0 ? 'Includes required 911 fees' : null,
            product.regularPrice !== undefined ? `original ${formatMoney(Number(product.regularPrice))}` : null
        ].filter(Boolean).join('; '),
        topBadges,
        chips: detailChips,
        badgeText: primaryTopBadge?.text || undefined,
        badgeColor: primaryTopBadge?.type || product.badgeColor || fallbackProduct.badgeColor || 'primary',
        highlightNote: product.display?.highlightNote || product.highlightNote || fallbackProduct.highlightNote,
        visible: product.visible !== false,
        status: product.status || 'active',
        startsAt: product.startsAt,
        expiresAt: product.expiresAt,
        archivedAt: product.archivedAt,
        visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
        visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
        quoteEligible: product.quoteEligible !== false,
        customerEmailEligible: Boolean(product.customerEmailEligible),
        emailDisplayName: product.emailDisplayName || fallbackProduct.emailDisplayName || product.displayName,
        emailBullets: Array.isArray(product.emailBullets) && product.emailBullets.length > 0
            ? product.emailBullets
            : fallbackProduct.emailBullets,
        emailIncludeInQuoteTable: product.customerEmailEligible !== false,
        emailCategory: 'homePhone'
    };
};

export const mapHomePhoneConfigProducts = (configProducts = [], fallbackProducts = [], options = {}) => configProducts
    .map((product) => normalizeConfigHomePhoneProduct(
        product,
        fallbackProducts.find((fallbackProduct) => fallbackProduct.id === product.id)
    ))
    .filter((product) => productIsAvailableForBuilder(product, options));

export const normalizeConfigInternetPlan = (product, fallbackPlan = {}) => {
    const detailChips = visibleDisplayChips(product, fallbackPlan.chips);
    const topBadges = visibleDisplayTopBadges(product);
    const primaryTopBadge = topBadges[0];
    const topBadgesConfigured = hasConfiguredDisplayChipEntries(product.display || {}, ['topBadges', 'cardBadges']);
    const internalNotes = Array.isArray(product.internalNotes) ? product.internalNotes : [];

    return {
        ...fallbackPlan,
        id: product.id,
        name: product.displayName || fallbackPlan.name || product.id,
        sortOrder: product.sortOrder ?? fallbackPlan.sortOrder ?? 999,
        belowLine: numberOrFallback(product.belowLinePrice, fallbackPlan.belowLine),
        aboveLine: nullableNumberOrFallback(product.aboveLinePrice, fallbackPlan.aboveLine ?? null),
        mobilityBelowLine: numberOrFallback(product.mobilityBelowLinePrice, fallbackPlan.mobilityBelowLine),
        mobilityAboveLine: nullableNumberOrFallback(product.mobilityAboveLinePrice, fallbackPlan.mobilityAboveLine ?? null),
        eppMobilityBelowLine: numberOrFallback(product.eppMobilityBelowLinePrice, fallbackPlan.eppMobilityBelowLine),
        eppMobilityAboveLine: nullableNumberOrFallback(product.eppMobilityAboveLinePrice, fallbackPlan.eppMobilityAboveLine ?? null),
        badgeText: primaryTopBadge?.text || undefined,
        badgeColor: primaryTopBadge?.type || product.badgeColor || fallbackPlan.badgeColor || 'primary',
        recommended: topBadgesConfigured ? false : Boolean(product.recommended),
        hot: topBadgesConfigured ? false : Boolean(product.hot),
        highlightNote: product.display?.highlightNote || product.highlightNote || fallbackPlan.highlightNote,
        notes: internalNotes.length > 0 ? internalNotes : fallbackPlan.notes || [],
        cardDescription: product.display?.cardDescription || internalNotes.join(' | ') || fallbackPlan.cardDescription,
        topBadges,
        chips: detailChips,
        visible: product.visible !== false,
        status: product.status || 'active',
        startsAt: product.startsAt,
        expiresAt: product.expiresAt,
        archivedAt: product.archivedAt,
        visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
        visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
        quoteEligible: product.quoteEligible !== false,
        customerEmailEligible: Boolean(product.customerEmailEligible),
        tvPriceAdjustment: normalizeTvPriceAdjustmentRule(product.tvPriceAdjustment || fallbackPlan.tvPriceAdjustment),
        pabAdjustmentAmount: numberOrFallback(product.pabAdjustmentAmount, fallbackPlan.pabAdjustmentAmount ?? PAB_SURCHARGE),
        emailRegularPrice: nullableNumberOrFallback(product.emailRegularPrice, fallbackPlan.emailRegularPrice ?? null),
        mobilityDiscountEligible: product.mobilityDiscountEligible !== false,
        eppEligible: product.eppEligible !== false,
        emailDisplayName: product.emailDisplayName || fallbackPlan.emailDisplayName || product.displayName,
        emailBullets: Array.isArray(product.emailBullets) && product.emailBullets.length > 0
            ? product.emailBullets
            : fallbackPlan.emailBullets,
        emailIncludeInQuoteTable: product.customerEmailEligible !== false,
        emailCategory: 'internet'
    };
};

export const mapInternetConfigProducts = (configProducts = [], fallbackProducts = [], options = {}) => configProducts
    .map((product) => normalizeConfigInternetPlan(
        product,
        fallbackProducts.find((fallbackProduct) => fallbackProduct.id === product.id)
    ))
    .filter((product) => productIsAvailableForBuilder(product, options));

export const normalizeConfigCatalogProduct = (product, fallbackProduct = {}, emailCategory = '') => {
    const detailChips = visibleDisplayChips(product, fallbackProduct.chips);
    const topBadges = visibleDisplayTopBadges(product);
    const primaryTopBadge = topBadges[0];
    const topBadgesConfigured = hasConfiguredDisplayChipEntries(product.display || {}, ['topBadges', 'cardBadges']);
    const internalNotes = Array.isArray(product.internalNotes) ? product.internalNotes : [];
    const linePricing = emailCategory === 'mobility'
        ? normalizeMobilityLinePricing(product, fallbackProduct)
        : product.linePricing ?? fallbackProduct.linePricing;
    const linePricingNote = emailCategory === 'mobility' ? formatMobilityLinePricingNote(linePricing) : null;
    const cardDescription = product.display?.cardDescription || '';

    return {
        ...fallbackProduct,
        id: product.id,
        name: product.displayName || fallbackProduct.name || product.id,
        sortOrder: product.sortOrder ?? fallbackProduct.sortOrder ?? 999,
        price: numberOrFallback(product.price, fallbackProduct.price),
        regularPrice: nullableNumberOrFallback(product.regularPrice, fallbackProduct.regularPrice ?? null),
        category: product.category ?? fallbackProduct.category,
        type: product.type ?? fallbackProduct.type,
        note: emailCategory === 'mobility'
            ? linePricing?.noteTemplate || linePricingNote || cardDescription || product.note || internalNotes.join('; ') || fallbackProduct.note
            : cardDescription || product.note || internalNotes.join('; ') || fallbackProduct.note,
        linePricing,
        linePricingNote,
        term: product.term ?? fallbackProduct.term,
        devices: product.devices ?? fallbackProduct.devices,
        allowQuantity: Boolean(product.allowQuantity ?? fallbackProduct.allowQuantity),
        complexPricing: Boolean(product.complexPricing ?? fallbackProduct.complexPricing),
        separateFlowOnly: Boolean(product.separateFlowOnly ?? fallbackProduct.separateFlowOnly),
        badgeText: primaryTopBadge?.text || undefined,
        badgeColor: primaryTopBadge?.type || product.badgeColor || fallbackProduct.badgeColor || 'primary',
        hot: topBadgesConfigured ? false : Boolean(product.hot ?? fallbackProduct.hot),
        recommended: topBadgesConfigured ? false : Boolean(product.recommended ?? fallbackProduct.recommended),
        highlightNote: product.display?.highlightNote || product.highlightNote || fallbackProduct.highlightNote,
        topBadges,
        chips: detailChips,
        visible: product.visible !== false,
        status: product.status || 'active',
        startsAt: product.startsAt,
        expiresAt: product.expiresAt,
        archivedAt: product.archivedAt,
        visibleInAdminBuilder: product.visibleInAdminBuilder !== false,
        visibleInAgentBuilder: product.visibleInAgentBuilder !== false,
        quoteEligible: product.quoteEligible !== false,
        customerEmailEligible: Boolean(product.customerEmailEligible),
        emailDisplayName: product.emailDisplayName || fallbackProduct.emailDisplayName || product.displayName,
        emailBullets: Array.isArray(product.emailBullets) && product.emailBullets.length > 0
            ? product.emailBullets
            : fallbackProduct.emailBullets,
        emailIncludeInQuoteTable: Boolean(product.customerEmailEligible),
        emailCategory
    };
};

export const mapCatalogConfigProducts = (configProducts = [], fallbackProducts = [], emailCategory = '', options = {}) => configProducts
    .map((product) => normalizeConfigCatalogProduct(
        product,
        fallbackProducts.find((fallbackProduct) => fallbackProduct.id === product.id),
        emailCategory
    ))
    .filter((product) => productIsAvailableForBuilder(product, options));

export const mapAddonsConfigGroups = (configGroups = [], fallbackGroups = [], options = {}) => configGroups
    .map((group) => {
        const fallbackGroup = fallbackGroups.find((candidate) => candidate.id === group.id) || {};
        const fallbackItems = fallbackGroup.items || [];
        const items = mapCatalogConfigProducts(group.items || [], fallbackItems, 'addon', options);

        return {
            ...fallbackGroup,
            id: group.id,
            title: group.title || fallbackGroup.title || group.id,
            sortOrder: group.sortOrder ?? fallbackGroup.sortOrder ?? 999,
            helperText: group.helperText || fallbackGroup.helperText,
            items
        };
    })
    .filter((group) => group.items.length > 0);

export const visibleTvDisplayTopBadges = (metadata = {}, fallbackPlan = {}) => {
    const configured = configuredDisplayChipEntries(metadata, ['topBadges', 'cardBadges']);
    const fallback = fallbackTopBadgeEntries({
        badges: fallbackPlan.badgeText ? [fallbackPlan.badgeText] : [],
        badgeColor: fallbackPlan.badgeColor || 'primary'
    });

    return visibleChipEntries(configured ?? fallback);
};

export const visibleTvDisplayChips = (metadata = {}, fallbackChips = []) => {
    const configured = configuredDisplayChipEntries(metadata, ['detailChips', 'featureChips', 'chips']);
    const fallback = (fallbackChips || []).map((chip, index) => (
        typeof chip === 'string'
            ? normalizeDisplayChipEntry({ text: chip, visible: true, order: index + 1 }, index)
            : normalizeDisplayChipEntry(chip, index)
    ));

    return visibleChipEntries(configured ?? fallback).map((chip) => chip.text);
};

export const normalizeTvDisplayMetadata = (metadata = {}, fallbackPlan = {}) => {
    const packageKey = metadata.packageKey || fallbackPlan.packageKey || '';
    const pvrMode = ['included', 'optional', 'none'].includes(metadata.pvrMode)
        ? metadata.pvrMode
        : fallbackPlan.pvrIncluded
            ? 'included'
            : 'optional';
    const pvrPrice = numberOrFallback(metadata.pvrPrice, pvrMode === 'included' ? 0 : PVR_PRICE);
    const topBadges = visibleTvDisplayTopBadges(metadata, fallbackPlan);
    const chips = visibleTvDisplayChips(metadata, fallbackPlan.chips);

    return {
        packageKey,
        displayNameOverride: metadata.displayNameOverride || fallbackPlan.name || packageKey,
        cardDescription: metadata.cardDescription || 'First TV box included/free',
        quoteSummaryNote: metadata.quoteSummaryNote || '',
        customerEmailName: metadata.customerEmailName || metadata.displayNameOverride || fallbackPlan.name,
        customerEmailBullets: Array.isArray(metadata.customerEmailBullets) && metadata.customerEmailBullets.length > 0
            ? metadata.customerEmailBullets
            : fallbackPlan.emailBullets || ['Included: TELUS TV Digital Box'],
        pvrMode,
        pvrPrice,
        topBadges,
        chips,
        visibleInManualFallback: metadata.visibleInManualFallback !== false,
        visibleInAgentBuilder: metadata.visibleInAgentBuilder !== false,
        visibleInAdminBuilder: metadata.visibleInAdminBuilder !== false,
        sortOrder: metadata.sortOrder ?? fallbackPlan.sortOrder ?? 999,
        internalAgentNote: metadata.internalAgentNote || '',
        customerEmailEligible: metadata.customerEmailEligible !== false,
        emailRegularReferencePrice: metadata.emailRegularReferencePrice,
        source: 'active'
    };
};

export const fallbackTvDisplayMetadata = (plan = {}) => normalizeTvDisplayMetadata({
    packageKey: plan.packageKey,
    displayNameOverride: plan.name,
    cardDescription: 'First TV box included/free',
    quoteSummaryNote: `First TV box included/free; ${plan.pvrIncluded ? 'PVR included' : 'PVR optional'}`,
    customerEmailName: plan.name,
    customerEmailBullets: plan.emailBullets || ['Included: TELUS TV Digital Box'],
    pvrMode: plan.pvrIncluded ? 'included' : 'optional',
    pvrPrice: plan.pvrIncluded ? 0 : PVR_PRICE,
    topBadges: [
        { text: plan.pvrIncluded ? 'PVR included' : (plan.badgeText || 'PVR optional'), type: plan.badgeColor || 'primary', visible: true, order: 1 }
    ],
    detailChips: [
        { text: 'Promo price', visible: true, order: 1 },
        { text: plan.pvrIncluded ? 'PVR included' : 'PVR optional: +$10', visible: true, order: 2 },
        { text: 'EPP -$5', visible: true, order: 3 }
    ],
    visibleInManualFallback: true,
    visibleInAgentBuilder: true,
    visibleInAdminBuilder: true,
    sortOrder: plan.sortOrder,
    customerEmailEligible: plan.emailIncludeInQuoteTable !== false,
    emailRegularReferencePrice: plan.regularPrice
}, plan);

export const mapTvDisplaySettings = (configPackages = [], fallbackPackages = tvPackages, options = {}) => {
    const metadataByKey = new Map((configPackages || []).map((metadata) => [metadata.packageKey, metadata]));

    return fallbackPackages.map((plan) => {
        const metadata = metadataByKey.has(plan.packageKey)
            ? normalizeTvDisplayMetadata(metadataByKey.get(plan.packageKey), plan)
            : fallbackTvDisplayMetadata(plan);

        return {
            ...plan,
            packageKey: plan.packageKey,
            name: metadata.displayNameOverride || plan.name,
            sortOrder: metadata.sortOrder ?? plan.sortOrder,
            pvrIncluded: metadata.pvrMode === 'included',
            pvrMode: metadata.pvrMode,
            pvrPrice: metadata.pvrPrice,
            badgeText: metadata.topBadges?.[0]?.text || undefined,
            badgeColor: metadata.topBadges?.[0]?.type || plan.badgeColor || 'primary',
            topBadges: metadata.topBadges,
            chips: metadata.chips,
            cardDescription: metadata.cardDescription,
            quoteSummaryNote: metadata.quoteSummaryNote,
            internalAgentNote: metadata.internalAgentNote,
            customerEmailEligible: metadata.customerEmailEligible,
            emailDisplayName: metadata.customerEmailName || plan.name,
            emailBullets: metadata.customerEmailBullets,
            emailIncludeInQuoteTable: metadata.customerEmailEligible,
            regularPrice: metadata.emailRegularReferencePrice ?? plan.regularPrice,
            visibleInManualFallback: metadata.visibleInManualFallback,
            visibleInAgentBuilder: metadata.visibleInAgentBuilder,
            visibleInAdminBuilder: metadata.visibleInAdminBuilder
        };
    }).filter((plan) => (
        plan.visibleInManualFallback !== false
        && (options.isAgentBeta ? plan.visibleInAgentBuilder !== false : plan.visibleInAdminBuilder !== false)
    ));
};

export const applyTvV2PackagePrices = (packages = [], priceTable = {}) => (
    packages.map((plan) => {
        const livePrice = plan.packageKey === 'core+pick5'
            ? priceTable?.['core+1premium'] ?? priceTable?.[plan.packageKey]
            : priceTable?.[plan.packageKey];
        const parsedLivePrice = Number(livePrice);

        if (!Number.isFinite(parsedLivePrice)) {
            return plan;
        }

        return {
            ...plan,
            price: parsedLivePrice
        };
    })
);

export const fetchTvV2PriceTable = async () => {
    try {
        const response = await fetch('/api/tv-packages-v2/pricing');

        if (!response.ok) {
            throw new Error(`TV pricing request failed with status ${response.status}`);
        }

        const data = await response.json();
        return data?.priceTable || {};
    } catch (error) {
        console.warn('Bundle Builder could not load TV V2 package pricing. Using fallback TV package prices.', error);
        return {};
    }
};

export const normalizeEmailShellSettings = (settings = {}) => {
    const merged = {
        ...fallbackEmailShell,
        ...settings,
        socialIconUrls: {
            neighbourhood: fallbackEmailShell.neighbourhoodIcon,
            facebook: fallbackEmailShell.facebookIcon,
            twitter: fallbackEmailShell.twitterIcon,
            ...(settings.socialIconUrls || {})
        }
    };
    const storeAddress = [
        merged.storeAddressLine1,
        merged.storeAddressLine2
    ].filter(Boolean).join(', ') || merged.storeAddress;

    return {
        ...merged,
        storeAddress,
        headerImage: merged.headerImageUrl || merged.headerImage,
        rabbitHeroImage: merged.rabbitHeroImageUrl || merged.rabbitHeroImage,
        onboardingImage: merged.onboardingBannerImageUrl || merged.onboardingImage,
        personIcon: merged.customerDetailsIconUrl || merged.personIcon,
        wifiIcon: merged.modemIconUrl || merged.wifiIcon,
        boxIcon: merged.tvBoxIconUrl || merged.boxIcon,
        pvrIcon: merged.pvrIconUrl || merged.boxIcon,
        installIcon: merged.starIconUrl || merged.installIcon,
        critterImage: merged.rabbitFooterImageUrl || merged.critterImage,
        neighbourhoodIcon: merged.socialIconUrls?.neighbourhood || merged.neighbourhoodIcon,
        facebookIcon: merged.socialIconUrls?.facebook || merged.facebookIcon,
        twitterIcon: merged.socialIconUrls?.twitter || merged.twitterIcon,
        footerStoreImage: merged.mapStoreImageUrl || merged.footerStoreImage,
        footerLegalHtml: merged.footerLegalText || merged.footerLegalHtml,
        footerLegalFinePrintHtml: merged.footerFinePrintText || merged.footerLegalFinePrintHtml
    };
};

export function getById(items, id) {
    return items.find((item) => item.id === id) || null;
}

export function getTvPackageKeyFromName(packageName = '') {
    const normalized = packageName.toLowerCase().replace(/\s+/g, ' ').trim();
    const hasTheme = normalized.includes('theme');
    const hasPremium = normalized.includes('premium');

    if (normalized === 'core') return 'core';
    if (!hasTheme && normalized.includes('1 premium')) return 'core+1premium';
    if (normalized.includes('11 theme') && normalized.includes('2 premium')) return 'core+theme11+2premium';
    if (normalized.includes('7 theme') && normalized.includes('1 premium')) return 'core+7theme+1premium';
    if (normalized.includes('4 theme') && normalized.includes('1 premium')) return 'core+4theme+1premium';
    if (normalized.includes('3 theme') && normalized.includes('2 premium')) return 'core+3theme+2premium';
    if (normalized.includes('2 theme') && normalized.includes('1 premium')) return 'core+2theme+1premium';
    if (normalized.includes('1 theme') && normalized.includes('2 premium')) return 'core+1theme+2premium';
    if (normalized.includes('3 theme') && !hasPremium) return 'core+3theme';

    return '';
}

export function buildTvRecommendationSelection(recommendation, context = {}) {
    if (!recommendation) {
        return null;
    }

    const fullRecommendation = context.recommendation || {};
    const selectedChannels = context.selectedChannels || [];
    const packageKey = recommendation.key || getTvPackageKeyFromName(recommendation.pkgStr);
    const manualTvId = tvPackageKeyToManualId[packageKey] || '';
    const manualPlan = getById(tvPackages, manualTvId);
    const basePackageName = recommendation.pkgStr || manualPlan?.name || 'Recommended TV package';
    const addOnCount = Number(recommendation.addOnCount || 0);
    const addOnTotal = Number(recommendation.addOnTotal || 0);
    const basePrice = Number(recommendation.basePrice ?? recommendation.price ?? recommendation.total ?? 0);
    const total = Number(recommendation.total ?? recommendation.price ?? (basePrice + addOnTotal));
    const addOnLabel = addOnCount > 0
        ? ` + ${addOnCount} extra theme pack${addOnCount === 1 ? '' : 's'}`
        : '';
    const displayName = `${basePackageName}${addOnLabel}`;
    const regularBasePrice = Number(manualPlan?.regularPrice ?? basePrice);
    const selectedChoices = selectedChannels.map((channel) => channel.name).filter(Boolean);
    const requiredThemePacks = fullRecommendation.requiredThemePacks || fullRecommendation.summary?.themePacks || [];
    const premiumChannels = fullRecommendation.premiumChannels || fullRecommendation.summary?.premiumChannels || [];
    const upgradeOpportunity = fullRecommendation.upsell && fullRecommendation.upsell.priceDiff > 0
        ? {
            packageName: fullRecommendation.upsell.pkgStr,
            price: Number(fullRecommendation.upsell.price || 0),
            priceDiff: Number(fullRecommendation.upsell.priceDiff || 0),
            extraThemes: Number(fullRecommendation.upsell.extraThemes || 0),
            extraPremiums: Number(fullRecommendation.upsell.extraPremiums || 0)
        }
        : null;
    const alternative = fullRecommendation.alternative
        ? {
            packageName: fullRecommendation.alternative.pkgStr,
            total: Number(fullRecommendation.alternative.total ?? fullRecommendation.alternative.price ?? fullRecommendation.alternative.basePrice ?? 0),
            addOnCount: Number(fullRecommendation.alternative.addOnCount || 0),
            addOnTotal: Number(fullRecommendation.alternative.addOnTotal || 0)
        }
        : null;

    return {
        id: `tv-v2-${packageKey || basePackageName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        name: displayName,
        basePackageName,
        source: 'tv-recommender',
        packageKey,
        manualTvId,
        recommendationKey: `${packageKey || basePackageName}-${total}-${addOnCount}`,
        price: total,
        basePrice,
        addOnCount,
        addOnTotal,
        addonUnitPrice: addOnCount > 0 ? addOnTotal / addOnCount : 0,
        regularPrice: regularBasePrice + addOnTotal,
        pvrIncluded: Boolean(manualPlan?.pvrIncluded),
        emailBullets: ['Included: TELUS TV Digital Box'],
        emailIncludeInQuoteTable: true,
        emailCategory: 'tv',
        recommendationSummary: {
            selectedChoices,
            requiredThemePacks,
            premiumChannels,
            bestValuePackage: basePackageName,
            addOnCount,
            addOnTotal,
            addonUnitPrice: addOnCount > 0 ? addOnTotal / addOnCount : 0,
            extraValueNote: recommendation.note || '',
            upgradeOpportunity,
            alternative
        }
    };
}
